import { Card, KpiCard } from '@/components';
import { useApi } from '@/api/hooks';
import styles from './Monitoring.module.css';

interface TenantUsage {
  slug: string;
  company_name: string;
  user_count: number;
  property_count: number;
  storage_used_gb: number;
  max_storage_gb: number;
  storage_pct: number;
}

interface MonitoringStats {
  dbConnections: number;
  dbMaxConnections: number;
  avgResponseMs: number;
  uptimePercent: number;
  apiCallsThisMonth: number;
  events: { type: string; action: string; details: string; created_at: string; company_name: string }[];
}

const eventDotColor: Record<string, string> = {
  tenant_created: 'var(--brand)',
  system: 'var(--green)',
  alert: 'var(--amber)',
  error: 'var(--red)',
};

function formatApiCalls(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function Monitoring() {
  const { data: tenants, loading } = useApi<TenantUsage[]>('/console/monitoring');
  const { data: monStats } = useApi<MonitoringStats>('/console/monitoring/stats');

  const totalStorage = tenants?.reduce((sum, t) => sum + Number(t.storage_used_gb || 0), 0) ?? 0;
  const totalMaxStorage = tenants?.reduce((sum, t) => sum + Number(t.max_storage_gb || 0), 0) ?? 0;
  const dbConnections = monStats?.dbConnections ?? 0;
  const dbMaxConnections = monStats?.dbMaxConnections ?? 100;

  const apiCalls = monStats?.apiCallsThisMonth ?? 0;
  const connPct = dbMaxConnections > 0 ? Math.round((dbConnections / dbMaxConnections) * 100) : 0;

  const uptime = monStats?.uptimePercent ?? 99.9;
  const avgResp = monStats?.avgResponseMs ?? 0;

  const kpis = [
    { label: 'システム稼働率（30日）', value: `${uptime}%`, delta: uptime >= 99.9 ? 'DB応答正常' : '要確認', trend: uptime >= 99.9 ? 'up' as const : 'down' as const },
    { label: '平均レスポンス', value: avgResp > 0 ? `${avgResp}ms` : '—', delta: avgResp > 0 && avgResp < 200 ? '良好' : '', trend: 'flat' as const },
    { label: 'DB接続数', value: `${dbConnections} / ${dbMaxConnections}`, delta: connPct > 70 ? `使用率 ${connPct}%` : '', trend: connPct > 70 ? 'down' as const : 'flat' as const },
    { label: '今月 API コール', value: formatApiCalls(apiCalls), delta: apiCalls > 0 ? 'login_history計測' : '', trend: 'flat' as const },
  ];

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  // 物件数の最大値
  const maxPropertyCount = Math.max(...(tenants ?? []).map((t) => t.property_count), 1);

  return (
    <div className={styles.page}>
      <div className={styles.kpiRow}>
        {kpis.map((k) => <KpiCard key={k.label} {...k} />)}
      </div>

      <div className={styles.grid2}>
        {/* 左: テナント別物件数 */}
        <Card title="📊 テナント別利用状況（物件数）">
          <div className={styles.usageList}>
            {(tenants ?? []).slice(0, 5).map((t) => {
              const pct = Math.round((t.property_count / maxPropertyCount) * 100);
              return (
                <div key={t.slug} className={styles.usageItem}>
                  <div className={styles.usageName}>{t.company_name}</div>
                  <div className={styles.usageBarRow}>
                    <div className={styles.barTrack}>
                      <div className={styles.barFill} style={{ width: `${pct}%`, background: pct > 70 ? 'var(--amber)' : 'var(--brand)' }} />
                    </div>
                    <span className={styles.usageVal}>{t.property_count}件 {pct}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* 右: DBスキーマ使用量 */}
        <Card title="🗄️ DB スキーマ使用量">
          <div className={styles.usageList}>
            {(tenants ?? []).slice(0, 4).map((t) => {
              const pct = totalMaxStorage > 0 ? Math.round((Number(t.storage_used_gb) / totalMaxStorage) * 100) : 0;
              return (
                <div key={t.slug} className={styles.usageItem}>
                  <div className={styles.usageName} style={{ fontFamily: 'var(--mono)', fontSize: '12px' }}>tenant_{t.slug}</div>
                  <div className={styles.usageBarRow}>
                    <div className={styles.barTrack}>
                      <div className={styles.barFill} style={{ width: `${Math.min(pct * 5, 100)}%`, background: Number(t.storage_used_gb) > (t.max_storage_gb * 0.7) ? 'var(--amber)' : 'var(--brand)' }} />
                    </div>
                    <span className={styles.usageVal}>{t.storage_used_gb} GB</span>
                  </div>
                </div>
              );
            })}
            {(tenants ?? []).length > 4 && (() => {
              const otherCount = (tenants ?? []).length - 4;
              const otherStorage = (tenants ?? []).slice(4).reduce((s, t) => s + Number(t.storage_used_gb || 0), 0);
              const otherPct = totalMaxStorage > 0 ? Math.round((otherStorage / totalMaxStorage) * 100) : 0;
              return (
                <div className={styles.usageItem}>
                  <div className={styles.usageName} style={{ fontFamily: 'var(--mono)', fontSize: '12px' }}>その他 {otherCount}スキーマ</div>
                  <div className={styles.usageBarRow}>
                    <div className={styles.barTrack}>
                      <div className={styles.barFill} style={{ width: `${Math.min(otherPct * 5, 100)}%` }} />
                    </div>
                    <span className={styles.usageVal}>{otherStorage.toFixed(1)} GB</span>
                  </div>
                </div>
              );
            })()}
            <div className={styles.totalRow}>
              <span>合計使用量</span>
              <span style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>{totalStorage.toFixed(1)} GB / {totalMaxStorage} GB <span style={{ color: 'var(--green)' }}>✓</span></span>
            </div>
          </div>
        </Card>
      </div>

      {/* システムイベント */}
      <Card title="⚡ 直近のシステムイベント">
        <div className={styles.eventList}>
          {(monStats?.events ?? []).length > 0 ? (
            (monStats?.events ?? []).map((e, i) => (
              <div key={i} className={styles.eventItem}>
                <div className={styles.eventDot} style={{ background: eventDotColor[e.type] ?? 'var(--brand)' }} />
                <span className={styles.eventText}>
                  {e.company_name ? `${e.company_name}: ` : ''}{e.action}{e.details ? ` — ${e.details}` : ''}
                </span>
                <span className={styles.eventTime}>{new Date(e.created_at).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))
          ) : (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink3)' }}>システムイベントなし</div>
          )}
        </div>
      </Card>
    </div>
  );
}
